// Build My Routine helper functions.
// Loaded after build-my-routine-config.js and before build-my-routine.js.

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return fallback;
  }
}

function normalizeValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
}

function splitListValues(value) {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitListValues(item));
  }

  if (value === null || value === undefined || value === '') return [];

  const raw = String(value).trim();
  if (!raw) return [];

  if (raw.startsWith('[') && raw.endsWith(']')) {
    const parsed = safeJsonParse(raw, null);
    if (Array.isArray(parsed)) return splitListValues(parsed);
  }

  return raw
    .split(/[•\n\r]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function cleanLabel(value) {
  return String(value || '').trim();
}

function fitzLabelToNumber(label) {
  const match = /type\s+(i{1,3}|iv|v|vi)\b/i.exec(String(label || ''));
  return match ? FITZ_ROMAN_TO_NUM[match[1].toLowerCase()] : '';
}

function formatMoney(cents) {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(cents / 100);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function createOptionCollector() {
  const items = [];
  const seen = new Set();

  return {
    add(label) {
      const clean = cleanLabel(label);
      const value = normalizeValue(clean);
      if (!clean || !value || seen.has(value)) return;
      seen.add(value);
      items.push({ value, label: clean });
    },
    values() {
      return items;
    }
  };
}

function orderedOptions(options, preferredOrder) {
  const orderMap = new Map(preferredOrder.map((label, index) => [normalizeValue(label), index]));

  return [...options].sort((a, b) => {
    const aIndex = orderMap.has(a.value) ? orderMap.get(a.value) : Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap.has(b.value) ? orderMap.get(b.value) : Number.MAX_SAFE_INTEGER;

    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.label.localeCompare(b.label);
  });
}

function buildOptionSets(sourceProducts) {
  const concernCollector = createOptionCollector();
  const skinProfileCollector = createOptionCollector();
  const skinSensitivityCollector = createOptionCollector();
  const routineCollector = createOptionCollector();
  const allergyCollector = createOptionCollector();
  const brandCollector = createOptionCollector();

  sourceProducts.forEach((product) => {
    concernCollector.add(product.main_concern_label);
    product.skin_profile_labels.forEach((label) => skinProfileCollector.add(label));
    product.skin_sensitivity_labels.forEach((label) => skinSensitivityCollector.add(label));
    routineCollector.add(product.routine_preference_label);
    brandCollector.add(product.vendor);

    product.allergies_labels.forEach((label) => allergyCollector.add(label));
  });

  return {
    concern: orderedOptions(concernCollector.values(), CONCERN_ORDER)
  .filter((option) => option.value !== normalizeValue('Sun Protection / UV Defence')),
    skin_profile: orderedOptions(skinProfileCollector.values(), SKIN_PROFILE_ORDER),
    skin_sensitivity: orderedOptions(skinSensitivityCollector.values(), SKIN_SENSITIVITY_ORDER),
    routine_preference: orderedOptions(routineCollector.values(), ROUTINE_PREFERENCE_ORDER),
current_actives: CURRENT_ACTIVES_LABELS
  .map((label) => ({ value: normalizeValue(label), label }))
  .sort((a, b) => a.label.localeCompare(b.label)),


allergies: orderedOptions(allergyCollector.values(), []),
    brands: orderedOptions(brandCollector.values(), [])
      .filter((opt) => opt.value !== 'skinstore')
  };
}
  function containsRetinolProduct(routineItems) {
  if (!routineItems || !routineItems.length) return false;

  const titleRe = /\bretinol\b|\bretinoid(s)?\b|\bretin[-\s]?a\b|\btretinoin\b|\badapalene\b|\btazarotene\b|\bretinal(dehyde)?\b|\bretinyl\s+(palmitate|retinoate|acetate|propionate)\b|\bgranactive\s+retinoid\b|\bhydroxypinacolone\s+retinoate\b|\bretinsphere\b|\bHPR\b/i;

  for (const item of routineItems) {
    const product = item && item.product;
    if (!product) continue;

    const activeGroups = toActiveSafetyGroups(product.active_ingredients || []);

    if (activeGroups.has('retinoids')) return true;

    if (titleRe.test(String(product.title || ''))) return true;
  }

  return false;
}

function getRoutineFamilyKey(product) {
  return product.routine_family_id || product.handle;
}

function collapseRoutineFamilies(list) {
  const seen = new Set();

  return list.filter((product) => {
    const key = getRoutineFamilyKey(product);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getRoutineFamilySiblings(product, products) {
  if (!product || !product.routine_family_id || !Array.isArray(products)) return [];

  return products
    .filter((candidate) => {
      if (!candidate.available) return false;
      if (candidate.slot !== product.slot) return false;
      if (candidate.vendor_slug !== product.vendor_slug) return false;
      return candidate.routine_family_id === product.routine_family_id;
    })
    .sort((a, b) =>
      getRoutineOptionLabel(a).localeCompare(getRoutineOptionLabel(b))
    );
}
function getRoutineOptionLabel(product) {
  if (product && product.routine_option_label) return product.routine_option_label;

  const title = String(product.title || '');
  const bracketMatch = title.match(/\(([^)]+)\)/);

  if (bracketMatch) {
    return bracketMatch[1].replace(/\bshade\b/gi, '').trim();
  }

  return title;
}
function renderSingleOptions(container, key, options, defaultLabel, onChange, rootEl) {
  if (!container) return;

  const currentValue = state[key];
  const defaultOption = options.find((option) => option.label === defaultLabel) || options[0];
  const selectedOption = options.find((option) => option.value === currentValue) || defaultOption;
  const selectedValue = selectedOption ? selectedOption.value : '';

  state[key] = selectedValue;

  container.innerHTML = options
    .map((option) => {
      const isActive = option.value === selectedValue;
      return `<button type="button" ${isActive ? 'class="is-active"' : ''} data-value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</button>`;
    })
    .join('');

  container.querySelectorAll('button').forEach((button) => {
    button.addEventListener('click', () => {
      container.querySelectorAll('button').forEach((item) => item.classList.remove('is-active'));
      button.classList.add('is-active');
      state[key] = button.dataset.value || '';

      if (key === 'routine_preference' && rootEl) {
        rootEl.dataset.routinePreference = state.routine_preference;
      }

      if (typeof onChange === 'function') onChange();
    });
  });

  if (key === 'routine_preference' && rootEl) {
    rootEl.dataset.routinePreference = state.routine_preference;
  }
}

function updateMultiDropdownLabel(container) {
  if (!container) return;

  const dropdown = container.closest('.build-my-routine-multi-dropdown');
  if (!dropdown) return;

  const labelEl = dropdown.querySelector('[data-multi-dropdown-label]');
  if (!labelEl) return;

  const selectedButtons = Array.from(container.querySelectorAll('button.is-active'))
    .filter((button) => button.dataset.value !== 'any')
    .filter((button) => button.dataset.clearMulti !== 'true');

  const clearBtn = container.querySelector('[data-clear-multi="true"]');
  if (clearBtn) {
    clearBtn.hidden = selectedButtons.length === 0;
  }

  if (!selectedButtons.length) {
    labelEl.textContent = labelEl.dataset.placeholder || 'Select options';
    return;
  }

  if (selectedButtons.length <= 2) {
    labelEl.textContent = selectedButtons
      .map((button) => button.textContent.trim())
      .join(', ');
    return;
  }

  labelEl.textContent = `${selectedButtons.length} selected`;
}

function renderMultiOptions(container, key, options, includeAny = false, onChange) {
  if (!container) return;

  const optionValues = new Set(options.map((option) => option.value));
  const currentValues = Array.isArray(state[key]) ? state[key] : [];

  let selectedValues = currentValues.filter((value) => optionValues.has(value));

  if (includeAny && (currentValues.includes('any') || selectedValues.length === 0)) {
    selectedValues = ['any'];
  }

  state[key] = selectedValues;

  const html = [];
  const isDropdown = Boolean(container.closest('.build-my-routine-multi-dropdown'));

  if (isDropdown) {
    const clearLabel = key === 'current_actives'
      ? 'Clear selected actives'
      : key === 'allergies'
        ? 'Clear selected reactions'
        : 'Clear selected';

    html.push(`
      <button
        type="button"
        class="build-my-routine-dropdown-clear"
        data-clear-multi="true"
        hidden
      >
        ${escapeHtml(clearLabel)}
      </button>
    `);
  }

  if (includeAny) {
    html.push(`<button type="button" ${state[key].includes('any') ? 'class="is-active"' : ''} data-value="any">Any</button>`);
  }

  options.forEach((option) => {
    const isActive = state[key].includes(option.value);
    html.push(`<button type="button" ${isActive ? 'class="is-active"' : ''} data-value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</button>`);
  });

  container.innerHTML = html.join('');

  container.querySelectorAll('button').forEach((button) => {
    button.addEventListener('click', () => {
      if (button.dataset.clearMulti === 'true') {
        state[key] = includeAny ? ['any'] : [];

        container.querySelectorAll('button').forEach((item) => {
          item.classList.toggle('is-active', includeAny && item.dataset.value === 'any');
        });

        updateMultiDropdownLabel(container);

        if (typeof onChange === 'function') onChange();
        return;
      }

      const value = button.dataset.value || '';

      if (includeAny && value === 'any') {
        state[key] = ['any'];

        container.querySelectorAll('button').forEach((item) => {
          item.classList.toggle('is-active', item.dataset.value === 'any');
        });

        updateMultiDropdownLabel(container);

        if (typeof onChange === 'function') onChange();
        return;
      }

      if (includeAny) {
        const anyButton = container.querySelector('[data-value="any"]');
        if (anyButton) anyButton.classList.remove('is-active');
        state[key] = state[key].filter((item) => item !== 'any');
      }

      button.classList.toggle('is-active');

      const selectedValues = Array.from(container.querySelectorAll('button.is-active'))
        .filter((item) => item.dataset.clearMulti !== 'true')
        .map((item) => item.dataset.value || '')
        .filter(Boolean)
        .filter((item) => item !== 'any');

      state[key] = selectedValues;

      if (includeAny && state[key].length === 0) {
        state[key] = ['any'];

        const anyButton = container.querySelector('[data-value="any"]');
        if (anyButton) anyButton.classList.add('is-active');
      }

      updateMultiDropdownLabel(container);

      if (typeof onChange === 'function') onChange();
    });
  });

  updateMultiDropdownLabel(container);
}


function brandLabelsForSlugs(slugs, optionSets) {
  const brandOptions = optionSets.brands || [];

  return slugs
    .map((slug) => {
      const match = brandOptions.find((opt) => opt.value === slug);
      return match ? match.label : slug;
    })
    .filter(Boolean);
}

function formatBrandList(labels) {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function buildRoutineSummary(routineItems, optionSets) {
  if (!routineItems.length) {
    return 'No exact match found yet based on the current metafield combinations.';
  }

  const plan = buildCurrentPlan();
  const isMakeupSlot = (slot) => String(slot || '').startsWith('makeup');
  const coreItems = routineItems.filter((item) => !isMakeupSlot(item.slot));

  const exactConcernMatch = routineItems.some((item) => item.product.main_concern === state.concern);

  const stepList = coreItems.map((item) => item.step).join(' → ');

  const cap = BUDGET_CAPS[state.budget];
  const total = routineItems.reduce((sum, item) => sum + item.product.price_cents, 0);
  const totalLabel = formatMoney(total);

  let budgetNote = '';

  if (cap === null || cap === undefined) {
    budgetNote = ` Total: ${totalLabel}.`;
  } else if (state.budget === 'under_700') {
    if (total > cap) {
      budgetNote = ` Total: ${totalLabel} — ${formatMoney(total - cap)} above your R700 budget. To fit, you can remove one of the optional steps below.`;
    } else if (total < cap) {
      budgetNote = ` Total: ${totalLabel} — ${formatMoney(cap - total)} under your R700 budget.`;
    } else {
      budgetNote = ` Total: ${totalLabel} — within your R700 budget.`;
    }
   } else if (state.budget === '700_2000') {
    const min = BUDGET_CAPS.under_700;
    const max = BUDGET_CAPS['700_2000'];

    if (total > max) {
      budgetNote = ` Total: ${totalLabel} — ${formatMoney(total - max)} above your selected range. To fit, you can remove one of the optional steps below.`;
    } else if (total < min) {
      budgetNote = ` Total: ${totalLabel} — below your selected range.`;
    } else {
      budgetNote = ` Total: ${totalLabel} — within your selected range.`;
    }
  }

  const concernNote = exactConcernMatch
    ? ''
    : ' Some steps use broader matches because we don\'t have an exact concern match for every slot.';

  return `A ${coreItems.length}-step routine is recommended for you (${stepList}).${budgetNote}${concernNote}`;
}
function buildRoutineFamilySelectorHtml(product, products) {
  const siblings = getRoutineFamilySiblings(product, products);

  if (siblings.length < 2) return '';

  return `
    <div class="build-my-routine-family-options">
      <span class="build-my-routine-family-options__label">Shade / option</span>
      <div class="build-my-routine-family-options__chips">
        ${siblings.map((sibling) => `
          <button
            type="button"
            class="build-my-routine-family-chip${sibling.handle === product.handle ? ' is-active' : ''}"
            data-routine-family-handle="${escapeHtml(sibling.handle)}"
          >
            ${escapeHtml(getRoutineOptionLabel(sibling))}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}
function applyPaediatricModeUi(isPaediatric, panel, root, renderConditionalGroups) {
  if (!panel) return;

const isTeen = state.age === '13-17';

panel.classList.toggle('is-paediatric', !!isPaediatric);
panel.classList.toggle('is-teen', isTeen);

if (typeof renderConditionalGroups === 'function') {
  renderConditionalGroups();
}

if (isTeen) {
  state.include_makeup = 'no';
  state.treatments = [];

  root.querySelectorAll('[data-group="include_makeup"] button').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.value === 'no');
  });

  root.querySelectorAll('[data-multi-group="treatments"]').forEach((groupEl) => {
    groupEl.querySelectorAll('button').forEach((btn) => {
      btn.classList.remove('is-active');
    });

    updateMultiDropdownLabel(groupEl);
  });
}

if (!isPaediatric) return;

  state.concern = '';
  state.routine_preference = 'am_plus_pm';
  state.include_makeup = 'no';
  state.recent_isotretinoin = 'no';
  state.topical_steroid_use = 'no';
  state.current_actives = [];
  state.treatments = [];

  if (state.budget === 'over_2000') {
    state.budget = '700_2000';

    root.querySelectorAll('[data-group="budget"] button').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.value === '700_2000');
    });
  }

  state.skin_sensitivity = normalizeValue(DEFAULTS.skin_sensitivity);

  ['concern', 'routine_preference'].forEach((groupKey) => {
    root.querySelectorAll(`[data-group="${groupKey}"] button`).forEach((btn) => {
      btn.classList.remove('is-active');
    });
  });

  root.querySelectorAll('[data-group="skin_sensitivity"] button').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.value === normalizeValue(DEFAULTS.skin_sensitivity));
  });

  ['include_makeup', 'recent_isotretinoin', 'topical_steroid_use'].forEach((groupKey) => {
    root.querySelectorAll(`[data-group="${groupKey}"] button`).forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.value === 'no');
    });
  });

['current_actives', 'treatments'].forEach((groupKey) => {
  root.querySelectorAll(`[data-multi-group="${groupKey}"]`).forEach((groupEl) => {
    groupEl.querySelectorAll('button').forEach((btn) => {
      btn.classList.remove('is-active');
    });

    updateMultiDropdownLabel(groupEl);
  });
});
}
console.log('[BuildMyRoutine] helpers loaded');